import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/api/client";
import { buildQuote, type QuoteResponse } from "@/api/scope";
import { formatApiError } from "@/utils/httpErrors";
import { PRICING_ENABLED } from "@/lib/pricing";
import { VERTICALS, OTHER_VERTICAL } from "@/lib/verticals";
import { type ScopeTier } from "@/lib/qoeCatalogue.generated";
import {
  type SizingBasis,
  SIZING_BASES,
  sizingToEbitda,
  legacyPricingBand,
  fmt,
  parseMillions,
} from "@/lib/engagementIntake";
import { useAddOnSelection } from "@/lib/useAddOnSelection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TierFeeCard from "@/components/engagement/TierFeeCard";
import AddOnsCard from "@/components/engagement/AddOnsCard";

// ENGAGE-PRICING-SPLIT-1: sizing (revenue/deal-size -> EBITDA proxy), the
// add-on registry and formatting helpers moved to lib/engagementIntake.ts —
// this file was 522 lines, over the FILE_SIZE_POLICY 400-line hard stop,
// and split-first was mandatory before any edit here. See that file's
// header for the full rationale.
//
// assignTier / pricingBand / pricingBandLabel / calculateBaseFee — the old
// flat-band, single-$3M-break client pricer — are DELETED outright, not
// moved there as a fallback. It was actively wrong ($14,000 vs the
// server's correct $9,516 at the EBITDA point that should have priced
// Standard). Tier and base fee now come from POST /qoe/quote
// (build_qoe_quote, apps/api/routers/qoe/catalogue.py) via buildQuote() in
// @/api/scope — the same server-authoritative call QuoteBuilder.tsx
// already uses, wired live below.
//
// NOTE (open gap, tracked separately as ENGAGE-ADDON-PRICING-1): add-on
// dollar amounts are still applied client-side (ADD_ONS in
// engagementIntake.ts). Their amounts are correct; only their location is
// wrong. total_fee therefore remains client-assembled (server base_fee +
// client add-on total) — this fix narrows the billing-integrity gap, it
// does not close it.
//
// TierFeeCard and AddOnsCard (components/engagement/) hold the "Tier & fee"
// and "Add-ons" JSX previously inline here — split out to bring this file
// back under the FILE_SIZE_POLICY 400-line hard stop. Both are pure
// presentational; all state (including the live buildQuote() call) stays
// here and is passed down as props.

// ── Component ──────────────────────────────────────────────────────────────────

export default function EngagementCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [businessName,   setBusinessName]   = useState("");
  const [engagementType, setEngagementType] = useState<string>("sell_side");
  const [vertical,       setVertical]       = useState<string>(OTHER_VERTICAL);
  const [sizingBasis,    setSizingBasis]    = useState<SizingBasis>("revenue");
  const [sizingInput,    setSizingInput]    = useState("");
  const [ebitdaInput,    setEbitdaInput]    = useState("");
  const [tierOverride,   setTierOverride]   = useState<ScopeTier | "auto">("auto");
  const [overrideReason, setOverrideReason] = useState("");
  const [submitting,     setSubmitting]     = useState(false);

  // Live server quote — tier recommendation and base fee both come from
  // here. Never computed locally; see the file-header note above.
  const [quote,        setQuote]        = useState<QuoteResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError,   setQuoteError]   = useState<string | null>(null);

  const sizingDollars  = useMemo(() => parseMillions(sizingInput), [sizingInput]);
  const ebitdaEstimate = useMemo(() => parseMillions(ebitdaInput), [ebitdaInput]);

  // ENGAGE-SIZING-1: the EBITDA figure the tier bands are defined on. Use the
  // CPA's own estimate when they have one; otherwise derive it from revenue or
  // deal size. Either way it sizes the engagement only — the reported EBITDA is
  // calculated later from the client's statements.
  const ebitdaDollars = useMemo(
    () => (ebitdaEstimate > 0 ? ebitdaEstimate : sizingToEbitda(sizingBasis, sizingDollars)),
    [ebitdaEstimate, sizingBasis, sizingDollars],
  );
  const sizedFromProxy = ebitdaEstimate <= 0 && ebitdaDollars > 0;

  // Debounced so a request doesn't fire on every digit typed into the sizing
  // or EBITDA field — 400ms after the figure (or the tier override) settles.
  // recommended_tier in the response is always the pure ebitda-based
  // recommendation regardless of an explicit `tier` on the request, so it
  // stays correct as the "Auto" badge even while a CPA override is active.
  useEffect(() => {
    if (ebitdaDollars <= 0) { setQuote(null); setQuoteError(null); return; }
    const handle = setTimeout(() => {
      setQuoteLoading(true);
      setQuoteError(null);
      buildQuote({
        ebitda: ebitdaDollars,
        tier: tierOverride === "auto" ? undefined : tierOverride,
      })
        .then(setQuote)
        .catch(err => { setQuote(null); setQuoteError(formatApiError(err)); })
        .finally(() => setQuoteLoading(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [ebitdaDollars, tierOverride]);

  const autoTier: ScopeTier = (quote?.recommended_tier as ScopeTier | undefined) ?? "lite";
  const effectiveTier: ScopeTier = tierOverride === "auto" ? autoTier : tierOverride;
  const isOverridden = tierOverride !== "auto" && tierOverride !== autoTier;

  const baseFee = quote?.base_fee ?? 0;

  // ENGAGE-ADDON-PRICING-1: selection + per-item price override state and
  // derived totals, extracted to lib/useAddOnSelection.ts to keep this file
  // under the FILE_SIZE_POLICY 400-line hard stop — see that file's header.
  const addOns = useAddOnSelection(effectiveTier, baseFee);

  // Server base fee + client add-on total. ENGAGE-ADDON-PRICING-1 (open,
  // tracked separately): add-ons are not yet priced server-side, so this
  // sum — not total_fee itself — is still client-assembled. See file header.
  const totalFee = baseFee + addOns.addOnTotal;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!businessName.trim()) { toast.error("Business name is required."); return; }
    if (ebitdaDollars <= 0) {
      // Never a dead end: name every figure that will size the engagement, so a
      // CPA who has revenue but no EBITDA knows the form will take it.
      toast.error(
        `Enter ${SIZING_BASES[sizingBasis].label.toLowerCase()} so we can size the engagement. ` +
        "An adjusted EBITDA estimate works too, if you have one.",
      );
      return;
    }
    if (!quote || quoteLoading) {
      toast.error("Pricing is still loading — wait a moment and try again.");
      return;
    }
    if (isOverridden && !overrideReason.trim()) {
      toast.error("A reason is required when overriding the automatic tier."); return;
    }
    // ENGAGE-ADDON-PRICING-1 step 2: same client-side gate as the tier
    // override above, mirrored per add-on. The server enforces this too
    // (crud.py) — this just avoids a round trip for the common typo.
    const missingReasonLabel = addOns.findOverrideMissingReason();
    if (missingReasonLabel) {
      toast.error(`A reason is required when overriding the price of "${missingReasonLabel}".`);
      return;
    }

    // analyst_name is required by the API and is the conflict-of-interest key,
    // so an empty string would create an unattributed engagement rather than
    // failing. ?? only falls through on null/undefined — an empty name would
    // have passed straight through.
    const analystName = (user?.name || user?.email || "").trim();
    if (!analystName) {
      toast.error("We could not identify the analyst on your account. Please sign out and sign back in, then try again.");
      return;
    }

    // ENGAGE-ADDON-PRICING-1 step 1/2/3: what the API stores against the deal
    // — every selected add-on with its base and final (possibly overridden)
    // price. Built from the same addOnFinalAmounts the running total and the
    // override-reason gate above both use, so the payload can never disagree
    // with what the CPA saw on screen.
    const selectedAddOnsPayload = addOns.buildPayload();

    setSubmitting(true);
    try {
      // ENGAGE-PRICING-SPLIT-1 R1/R2: tier is created via the legacy 2-value
      // pricing band (essentials|comprehensive), kept for back-compat — see
      // legacyPricingBand()'s header comment in engagementIntake.ts — then
      // immediately re-scoped to the real 3-value scope tier via the PATCH
      // /scope call below. base_fee is the live server quote for
      // effectiveTier, not a client formula. pricing_model is no longer
      // sent — the server no longer needs the client to name its own band.
      const res = await apiClient.post<{ deal_id: string }>("/qoe/deals", {
        business_name:   businessName.trim(),
        engagement_type: engagementType,
        analyst_name:    analystName,
        // ENGAGE-SIZING-1: the industry drives the benchmark card on the deal and
        // the industry overlay on the seller's document request list.
        vertical:        vertical,
        tier:            legacyPricingBand(effectiveTier),
        // Only the CPA's own figure — never the sizing proxy, which must not be
        // mistaken downstream for a reported EBITDA.
        ebitda_estimate: ebitdaEstimate > 0 ? ebitdaEstimate : null,
        sizing_basis:    sizingBasis,
        sizing_amount:   sizingDollars || null,
        base_fee:        quote.base_fee,
        total_fee:       totalFee,
        engagement_fee:  totalFee,
        // P0-3: tier_override is the FLAG, override_reason is the text. It used
        // to send the reason string as tier_override, which the API dropped
        // entirely — so the "logged to audit trail" promise below the field was
        // false. Both are now persisted and an override emits an audit event.
        tier_override:   isOverridden,
        override_reason: isOverridden ? overrideReason.trim() : null,
        // ENGAGE-ADDON-PRICING-1 step 1/2/3.
        selected_add_ons: selectedAddOnsPayload.length > 0 ? selectedAddOnsPayload : null,
      });
      const dealId = res.data.deal_id;
      toast.success(`Engagement created — ${dealId}`);

      // Re-scope to the real 3-value tier. deals.scope_tier defaults to
      // "focused" (Standard, 54-point) on every new deal until this runs —
      // loud on failure, because a silently-swallowed PATCH here would leave
      // e.g. a CPA-chosen Essentials engagement mis-scoped as Standard with
      // no sign anything went wrong. The deal itself already exists either
      // way, so this is a follow-up correction, not a rollback trigger.
      try {
        await apiClient.patch(`/qoe/deals/${dealId}/scope`, {
          scope_tier: effectiveTier,
          score: null,
          override: isOverridden,
          override_note: isOverridden ? overrideReason.trim() : null,
        });
      } catch (scopeErr) {
        toast.error(
          `Engagement created, but its scope tier could not be set (${formatApiError(scopeErr)}). ` +
          "It defaulted to Standard — open the deal and re-scope it manually.",
        );
      }

      navigate(`/dashboard`);
    } catch (err: unknown) {
      const status = (err as { response?: { status: number } })?.response?.status;
      if (status === 409) {
        toast.error(`A deal for "${businessName.trim()}" already exists on the opposite side of this engagement (conflict of interest).`);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">New Engagement</h1>
        <p className="text-sm text-gray-500 mt-1">
          {PRICING_ENABLED
            ? "Tier and fee are computed automatically from the size of the business — revenue or deal size. An EBITDA estimate is optional."
            : "Tier is assigned automatically from the size of the business — revenue or deal size. An EBITDA estimate is optional."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Deal basics */}
        <Card>
          <CardHeader><CardTitle>Deal Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="business_name">Business Name *</Label>
              <Input
                id="business_name"
                value={businessName}
                onChange={e => setBusinessName(e.target.value)}
                placeholder="Lone Star HVAC Services LLC"
                required
              />
            </div>
            <div>
              <Label htmlFor="engagement_type">Engagement Type</Label>
              <Select value={engagementType} onValueChange={v => { if (v) setEngagementType(v); }}>
                <SelectTrigger id="engagement_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sell_side">Sell-Side</SelectItem>
                  <SelectItem value="buy_side">Buy-Side</SelectItem>
                  <SelectItem value="lender">Lender Diligence</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="vertical">Industry</Label>
              <Select value={vertical} onValueChange={v => { if (v) setVertical(v); }}>
                <SelectTrigger id="vertical">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VERTICALS.map(v => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-gray-500">
                Sets the industry benchmark on the deal and tailors the document
                request list sent to the seller. Leave as “{OTHER_VERTICAL}” if unsure.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tier & fee */}
        <TierFeeCard
          sizingBasis={sizingBasis} setSizingBasis={setSizingBasis}
          sizingInput={sizingInput} setSizingInput={setSizingInput}
          sizingDollars={sizingDollars}
          ebitdaInput={ebitdaInput} setEbitdaInput={setEbitdaInput}
          ebitdaEstimate={ebitdaEstimate} ebitdaDollars={ebitdaDollars}
          sizedFromProxy={sizedFromProxy}
          isSuperAdmin={isSuperAdmin}
          autoTier={autoTier} effectiveTier={effectiveTier}
          tierOverride={tierOverride} setTierOverride={setTierOverride}
          isOverridden={isOverridden}
          overrideReason={overrideReason} setOverrideReason={setOverrideReason}
          quote={quote} quoteLoading={quoteLoading} quoteError={quoteError}
          baseFee={baseFee}
        />

        {/* Add-ons */}
        {ebitdaDollars > 0 && (
          <AddOnsCard
            effectiveTier={effectiveTier}
            selectedAddOns={addOns.selectedAddOns}
            toggleAddOn={addOns.toggleAddOn}
            baseFee={baseFee}
            isSuperAdmin={isSuperAdmin}
            addOnOverrideAmounts={addOns.addOnOverrideAmounts}
            setAddOnOverrideAmount={addOns.setAddOnOverrideAmount}
            addOnOverrideReasons={addOns.addOnOverrideReasons}
            setAddOnOverrideReason={addOns.setAddOnOverrideReason}
          />
        )}

        {/* Fee summary — QOE_PRICING_ENABLED gated. The fee is still computed and
            posted to the API; only the display is suppressed. */}
        {PRICING_ENABLED && ebitdaDollars > 0 && (
          <div className="rounded-xl border-2 border-[--color-primary] bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">Base fee</span>
              <span className="text-sm text-gray-900">
                {quoteLoading ? "…" : quote ? fmt(baseFee) : "—"}
              </span>
            </div>
            {addOns.addOnTotal > 0 && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-600">Add-ons</span>
                <span className="text-sm text-gray-900">{fmt(addOns.addOnTotal)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t pt-3">
              <span className="font-bold text-gray-900">Total Fee</span>
              <span className="text-xl font-bold text-[--color-primary]">{fmt(totalFee)}</span>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              Estimate only. Final fee subject to engagement letter.
            </p>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          {/* The button stays live with the sizing figure missing: a disabled
              submit with no explanation is the dead end. Submitting says, in
              business language, exactly which figure is needed. */}
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create Engagement"}
          </Button>
        </div>
      </form>
    </div>
  );
}
